import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const TOAST_DURATION_MS = 2_600;

export interface ValueChange {
  label: string;
  delta: number;
}

type ToastContent =
  | { kind: "text"; text: string }
  | { kind: "values"; changes: readonly ValueChange[] };

interface ToastState {
  id: number;
  content: ToastContent;
}

interface ToastActions {
  showToast: (text: string) => void;
  showValueChanges: (changes: readonly ValueChange[]) => void;
}

const ToastContext = createContext<ToastActions | null>(null);

const signedDelta = (delta: number): string => `${delta >= 0 ? "+" : ""}${delta}`;

/**
 * 全站通用的短时飘窗。
 *
 * 普通操作回执传文字；灵力、羁绊、好感等数值变化传结构化 changes，组件会把
 * 非零变化排成一行。变化量为 0 表示没有变化，不显示，也不做无障碍播报。
 * 显示位置、透明度、动画与无障碍播报只在这里维护。
 */
export function FloatingToast({ content }: { content: ToastContent }) {
  return (
    <p
      className={`floating-toast floating-toast--${content.kind}`}
      role="status"
      aria-live="polite"
    >
      {content.kind === "text" ? (
        content.text
      ) : (
        <span className="floating-toast__values">
          {content.changes.map((change, index) => (
            <span key={`${change.label}-${index}`} className="floating-toast__value">
              {index > 0 ? <span className="floating-toast__separator"> · </span> : null}
              {change.label} {signedDelta(change.delta)}
            </span>
          ))}
        </span>
      )}
    </p>
  );
}

/** 全局只挂一个宿主，连续提示会替换内容并重新播放动画。 */
export function FloatingToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const nextId = useRef(0);
  const timer = useRef<number | null>(null);

  const show = useCallback((content: ToastContent) => {
    nextId.current += 1;
    setToast({ id: nextId.current, content });
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const showToast = useCallback((text: string) => show({ kind: "text", text }), [show]);
  const showValueChanges = useCallback(
    (changes: readonly ValueChange[]) => {
      const nonZeroChanges = changes.filter((change) => change.delta !== 0);
      if (nonZeroChanges.length > 0) show({ kind: "values", changes: nonZeroChanges });
    },
    [show],
  );

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast, showValueChanges }}>
      {children}
      {toast ? <FloatingToast key={toast.id} content={toast.content} /> : null}
    </ToastContext.Provider>
  );
}

export function useFloatingToast(): ToastActions {
  const actions = useContext(ToastContext);
  if (!actions) throw new Error("useFloatingToast 必须在 FloatingToastProvider 内使用");
  return actions;
}
